import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useVideo } from '../context/VideoContext';
import VideoCard from '../components/VideoCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiRefreshCw, FiWifiOff, FiAlertTriangle, FiSearch } = FiIcons;

const ErrorPage = ({ errorCode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { videos } = useVideo();

  // Determine error type based on prop or route state
  const errorType = errorCode || location.state?.errorCode || '404';

  const errorContent = {
    '404': {
      title: 'Lost in Space',
      message: "The page you're looking for has drifted out of orbit. Let's get you back home.",
      icon: FiSearch,
      color: 'text-orange-500'
    },
    '500': {
      title: 'System Glitch',
      message: "Our servers are experiencing a momentary lapse in excellence. We're on it!",
      icon: FiAlertTriangle,
      color: 'text-red-500'
    },
    'offline': {
      title: 'Connection Lost',
      message: "It looks like your internet connection went on a break. Check your cables!",
      icon: FiWifiOff,
      color: 'text-zinc-500'
    }
  }[errorType] || {
    title: 'Unexpected Error',
    message: "Something went wrong, but we're not quite sure what. Try refreshing?",
    icon: FiAlertTriangle,
    color: 'text-amber-500'
  };

  const suggestedVideos = videos ? videos.slice(0, 4) : [];

  return (
    <div className="min-h-screen bg-[#09090b] pt-32 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Error Hero */}
        <div className="text-center mb-24">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`inline-flex p-6 rounded-[32px] bg-white/5 border border-white/10 mb-8 ${errorContent.color}`}
          >
            <SafeIcon icon={errorContent.icon} className="text-6xl" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-black text-white mb-6 uppercase tracking-tighter"
          >
            {errorContent.title}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-400 max-w-2xl mx-auto mb-12 font-medium"
          >
            {errorContent.message}
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4"
          >
            {errorType !== 'offline' && (
              <button 
                onClick={() => navigate('/')}
                className="btn-primary flex items-center gap-2 px-8 py-4"
              >
                <SafeIcon icon={FiHome} /> Return Home
              </button>
            )}
            <button 
              onClick={() => window.location.reload()}
              className="btn-secondary flex items-center gap-2 px-8 py-4"
            >
              <SafeIcon icon={FiRefreshCw} /> Try Again
            </button>
          </motion.div>
        </div>

        {/* Suggested Content - Only show if we have videos */}
        {suggestedVideos.length > 0 && (
          <div className="pt-20 border-t border-white/5">
            <div className="flex justify-between items-end mb-12">
              <div>
                <h2 className="text-white/30 text-xs font-black uppercase tracking-[0.25em] mb-2">Still want to explore?</h2>
                <h3 className="text-3xl font-black text-white uppercase tracking-tight">Recommended Curation</h3>
              </div>
              <button 
                onClick={() => navigate('/')}
                className="text-orange-500 font-bold text-sm hover:underline"
              >
                View Full Gallery →
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {suggestedVideos.map((video, idx) => (
                <VideoCard key={video.id} video={video} index={idx} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;